import { useState } from 'react';

import { saveChannelSettings } from './api/channelSettings.api';
import { ChannelAiAnswerSection } from './channel/ChannelAiAnswerSection';
import { ChannelBotMessagesSection } from './channel/ChannelBotMessagesSection';
import { ChannelCategorySourceSection } from './channel/ChannelCategorySourceSection';
import { ChannelContextSection } from './channel/ChannelContextSection';
import { ChannelCustomLabelsSection } from './channel/ChannelCustomLabelsSection';
import { ChannelDefaultCategorySection } from './channel/ChannelDefaultCategorySection';
import { ChannelEditorsSection } from './channel/ChannelEditorsSection';
import { ChannelInstructionsSection } from './channel/ChannelInstructionsSection';
import { ChannelQuestionsToIgnoreSection } from './channel/ChannelQuestionsToIgnoreSection';
import { ChannelRecencyBiasSection } from './channel/ChannelRecencyBiasSection';
import { ChannelSupportModeSection } from './channel/ChannelSupportModeSection';
import { useChannelSettingsForm } from './hooks/useChannelSettingsForm';
import { PROMPT_CLASSIFY_IGNORED } from '@core/const/const.PROMPT_CLASSIFY_IGNORED';
import type { DBChannel } from '@core/types/types.db';
import type { SettingsChannelEditorsEndpoint } from '@core/types/types.endpoint.settings';
import { Button } from '@frontend/components/ui/button';
import { Separator } from '@frontend/components/ui/separator';
import { useDirectoryUpdater } from '@frontend/shared/hooks/shared.hook.useDirectoryUpdater';
import { useFlag } from '@frontend/shared/hooks/shared.hook.useFlag';
import {
  DirectoryContext,
  useDirectoryCategories,
  useDirectoryTeam,
  useDirectoryUsers,
} from '@frontend/shared/hooks/useDirectory';
import { useRequiredContext } from '@frontend/shared/hooks/useRequiredContext';
import { useSubmitter } from '@frontend/shared/hooks/useSubmitter';
import { callApi } from '@frontend/shared/util/util.callApi';

export function SettingsChannelPrompt({
  channel,
  onChannelUpdate,
  onClose,
  isReadOnly = false,
}: {
  channel: DBChannel;
  onChannelUpdate: (updatedChannel: DBChannel) => void;
  onClose?: () => void;
  isReadOnly?: boolean;
}) {
  const team = useDirectoryTeam();
  const categories = useDirectoryCategories();
  const allUsers = useDirectoryUsers();
  const updateDirectory = useDirectoryUpdater();
  const { channels: allChannels } = useRequiredContext(DirectoryContext);

  const teamEditorIds = [
    ...(team.editors ?? []),
    ...(team.admins ?? []),
    team.created_by,
  ].filter((id, i, arr) => Boolean(id) && arr.indexOf(id) === i);
  const initialEditorIds = allUsers
    .filter(
      (u) =>
        teamEditorIds.includes(u.id) &&
        (u.expert_channels ?? []).includes(channel.id),
    )
    .map((u) => u.id);

  const [editorIds, setEditorIds] = useState(initialEditorIds);

  const editorsDifferent =
    editorIds.length !== initialEditorIds.length ||
    editorIds.some((id) => !initialEditorIds.includes(id)) ||
    initialEditorIds.some((id) => !editorIds.includes(id));

  // Feature flags for conditional rendering
  const teamSupportModeEnabled = useFlag('support_mode');
  const teamRespondToBotMessagesEnabled = useFlag('respond_to_bot_messages');
  const teamAiAnswerEnabled = useFlag('ai_answer');
  const teamRecencyBiasEnabled = team?.recency_bias ?? true; // Default to true

  // Use custom hook for form state management
  const { formState, setters, computed, actions } = useChannelSettingsForm(
    channel,
    team,
  );

  // Save handler
  const saveSettings = useSubmitter(async () => {
    const [updatedChannel] = await Promise.all([
      saveChannelSettings(channel.id, formState, computed.teamDefault),
      editorsDifferent
        ? callApi<SettingsChannelEditorsEndpoint>('/settings/channelEditors', {
            channelId: channel.id,
            editorIds,
          })
        : Promise.resolve(),
    ]);

    const directoryPatch: Parameters<typeof updateDirectory>[0] = {};

    if (updatedChannel) {
      directoryPatch.channels = allChannels.map((c) =>
        c.id === updatedChannel.id ? updatedChannel : c,
      );
    }

    if (editorsDifferent) {
      directoryPatch.users = allUsers.map((user) => {
        if (!teamEditorIds.includes(user.id)) return user;
        const shouldBeAssigned = editorIds.includes(user.id);
        const isAssigned = (user.expert_channels ?? []).includes(channel.id);
        if (shouldBeAssigned === isAssigned) return user;
        const newChannels = shouldBeAssigned
          ? [...(user.expert_channels ?? []), channel.id]
          : (user.expert_channels ?? []).filter((id) => id !== channel.id);
        return { ...user, expert_channels: newChannels };
      });
    }

    if (Object.keys(directoryPatch).length > 0) {
      updateDirectory(directoryPatch);
    }

    if (updatedChannel) {
      onChannelUpdate(updatedChannel);
    }
    onClose?.();
  });

  const handleCancel = () => {
    actions.resetForm();
    setEditorIds(initialEditorIds);
    onClose?.();
  };

  // Helper for category selection
  const handleCategoryToggle = (categoryId: string, checked: boolean) => {
    if (checked) {
      setters.setSelectedCategories([
        ...formState.selectedCategories,
        categoryId,
      ]);
    } else {
      setters.setSelectedCategories(
        formState.selectedCategories.filter((id) => id !== categoryId),
      );
    }
  };

  // Helper for category selection type change
  const handleCategorySelectionTypeChange = (
    type: 'all' | 'none' | 'specific',
  ) => {
    setters.setCategorySelectionType(type);
    // Reset selected categories when changing type
    if (type !== 'specific') {
      setters.setSelectedCategories([]);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      {/* Custom Instructions */}
      <ChannelInstructionsSection
        isEnabled={formState.instructionsOverride}
        value={formState.instructionsValue}
        onToggle={(enabled) => {
          setters.setInstructionsOverride(enabled);
          if (enabled) {
            setters.setInstructionsValue(computed.teamDefault);
          } else {
            setters.setInstructionsValue(computed.teamDefault);
          }
        }}
        onValueChange={setters.setInstructionsValue}
        isReadOnly={isReadOnly}
      />

      <Separator />

      {/* Channel Context & Team Context */}
      <ChannelContextSection
        contextEnabled={formState.contextOverride}
        contextValue={formState.contextValue}
        onContextToggle={(enabled) => {
          setters.setContextOverride(enabled);
          if (!enabled) {
            setters.setContextValue('');
          }
        }}
        onContextChange={setters.setContextValue}
        teamContextExcluded={formState.teamContextToggle}
        onTeamContextExcludedChange={setters.setTeamContextToggle}
        isReadOnly={isReadOnly}
      />

      <Separator />

      {/* Questions to Ignore */}
      <ChannelQuestionsToIgnoreSection
        isEnabled={formState.questionsToIgnoreOverride}
        value={formState.questionsToIgnoreValue}
        onToggle={(enabled) => {
          setters.setQuestionsToIgnoreOverride(enabled);
          if (!enabled) {
            setters.setQuestionsToIgnoreValue(
              computed.teamQuestionsToIgnore || PROMPT_CLASSIFY_IGNORED,
            );
          }
        }}
        onValueChange={setters.setQuestionsToIgnoreValue}
        isReadOnly={isReadOnly}
      />

      <Separator />

      {/* Answer Source Categories */}
      <ChannelCategorySourceSection
        selectionType={formState.categorySelectionType}
        selectedCategories={formState.selectedCategories}
        categories={categories}
        onSelectionTypeChange={handleCategorySelectionTypeChange}
        onCategoryToggle={handleCategoryToggle}
        isReadOnly={isReadOnly}
      />

      <Separator />

      {/* Default Save Category */}
      <ChannelDefaultCategorySection
        isOverridden={formState.defaultCategoryOverride}
        value={formState.defaultCategoryValue}
        onToggle={(enabled) => {
          setters.setDefaultCategoryOverride(enabled);
          if (!enabled) {
            setters.setDefaultCategoryValue(team.default_category_id);
          }
        }}
        onValueChange={setters.setDefaultCategoryValue}
        isReadOnly={isReadOnly}
      />

      <Separator />

      {/* Support Mode */}
      <ChannelSupportModeSection
        value={formState.supportModeValue}
        onValueChange={setters.setSupportModeValue}
        globalEnabled={teamSupportModeEnabled}
        isReadOnly={isReadOnly}
      />

      <Separator />

      {/* Bot Answering */}
      <ChannelAiAnswerSection
        value={formState.aiAnswerValue}
        onValueChange={setters.setAiAnswerValue}
        globalEnabled={teamAiAnswerEnabled}
        isReadOnly={isReadOnly}
      />

      <Separator />

      {/* Respond to Bot Messages */}
      <ChannelBotMessagesSection
        value={formState.respondToBotMessagesValue}
        onValueChange={setters.setRespondToBotMessagesValue}
        globalEnabled={teamRespondToBotMessagesEnabled}
        isReadOnly={isReadOnly}
      />

      <Separator />

      {/* Recency Bias */}
      <ChannelRecencyBiasSection
        value={formState.recencyBiasValue}
        onValueChange={setters.setRecencyBiasValue}
        globalEnabled={teamRecencyBiasEnabled}
        isReadOnly={isReadOnly}
      />

      <Separator />

      {/* Custom Labels - Only show when support mode is enabled */}
      {(teamSupportModeEnabled || channel.support_mode_override === true) && (
        <>
          <ChannelCustomLabelsSection
            notFoundOverride={formState.notFoundLabelOverride}
            notFoundValue={formState.supportMessageNotFoundValue}
            onNotFoundToggle={(enabled) => {
              setters.setNotFoundLabelOverride(enabled);
              if (enabled) {
                setters.setSupportMessageNotFoundValue(
                  team?.labels?.support_message_not_found || '',
                );
              }
            }}
            onNotFoundChange={setters.setSupportMessageNotFoundValue}
            notHelpfulOverride={formState.notHelpfulLabelOverride}
            notHelpfulValue={formState.supportMessageNotHelpfulValue}
            onNotHelpfulToggle={(enabled) => {
              setters.setNotHelpfulLabelOverride(enabled);
              if (enabled) {
                setters.setSupportMessageNotHelpfulValue(
                  team?.labels?.support_message_not_helpful || '',
                );
              }
            }}
            onNotHelpfulChange={setters.setSupportMessageNotHelpfulValue}
            isReadOnly={isReadOnly}
          />

          <Separator />
        </>
      )}

      {/* Editors */}
      <ChannelEditorsSection
        selectedIds={editorIds}
        setSelectedIds={setEditorIds}
        isReadOnly={isReadOnly}
      />

      <Separator />

      {/* Action Buttons */}
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={handleCancel} disabled={isReadOnly}>
          Cancel
        </Button>
        <Button
          onClick={saveSettings.submit}
          disabled={
            (!computed.hasChanges && !editorsDifferent) ||
            saveSettings.isSubmitting ||
            isReadOnly
          }
        >
          {saveSettings.isSubmitting ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    </div>
  );
}
